import React, { useState, useEffect, useCallback } from "react";
import { collection, getDocs, doc, getDoc, setDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { getEmailDomain } from "../services/authService";
import "./Signups.css"; // Reuse Signups styles

const OrgDashboard = () => {
  const { currentUser } = useAuth();
  const [metrics, setMetrics] = useState({
    dau: 0,
    mau: 0,
    signupsToday: 0,
    signupsLast7Days: 0,
    signupsLast30Days: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [orgDomain, setOrgDomain] = useState(null);

  const loadCachedMetrics = useCallback(async (domain) => {
    try {
      setLoading(true);
      setError(null);

      // Load cached metrics from analytics/orgSignups/{domain} document
      const signupsDocRef = doc(db, "analytics", `orgSignups_${domain}`);
      const signupsDoc = await getDoc(signupsDocRef);

      if (signupsDoc.exists()) {
        const data = signupsDoc.data();
        setMetrics({
          dau: data.dau || 0,
          mau: data.mau || 0,
          signupsToday: data.signupsToday || 0,
          signupsLast7Days: data.signupsLast7Days || 0,
          signupsLast30Days: data.signupsLast30Days || 0,
        });
        setLastUpdated(data.lastUpdated?.toDate() || null);
        console.log(
          "Loaded cached org signups metrics from:",
          data.lastUpdated?.toDate()
        );
      } else {
        console.log("No cached metrics found, calculating fresh...");
        await calculateAndSaveMetrics(domain);
      }
    } catch (err) {
      console.error("Error loading cached metrics:", err);
      setError("Failed to load metrics. Please try again.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOrgDomain = useCallback(async () => {
    if (!currentUser?.email) return;

    const domain = getEmailDomain(currentUser.email);
    if (domain) {
      setOrgDomain(domain);
      loadCachedMetrics(domain);
    }
  }, [currentUser, loadCachedMetrics]);

  useEffect(() => {
    loadOrgDomain();
  }, [loadOrgDomain]);

  const handleRefresh = async () => {
    if (!orgDomain) return;
    setIsRefreshing(true);
    await calculateAndSaveMetrics(orgDomain);
    setIsRefreshing(false);
  };

  const calculateAndSaveMetrics = async (domain) => {
    try {
      if (!isRefreshing) {
        setLoading(true);
      }
      setError(null);

      // Get current time boundaries for signup metrics
      const now = new Date();
      const todayStart = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      );
      const last7DaysStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const last30DaysStart = new Date(
        now.getTime() - 30 * 24 * 60 * 60 * 1000
      );

      // Fetch DAU and MAU from Cloud Function (using GA4 first_open events)
      // Note: This will be all users, not filtered by domain.
      // For org-specific DAU/MAU, you'd need to filter in the function or use a different approach
      const functions = getFunctions();
      const getAnalyticsMetrics = httpsCallable(
        functions,
        "getAnalyticsMetrics"
      );

      let dau = 0;
      let mau = 0;

      try {
        const analyticsResult = await getAnalyticsMetrics();
        dau = analyticsResult.data.dau || 0;
        mau = analyticsResult.data.mau || 0;
        // Note: These are global metrics. For org-specific, you'd need to filter by domain
      } catch (analyticsError) {
        console.error(
          "Error fetching analytics from Cloud Function:",
          analyticsError
        );
        // Continue with signup metrics even if analytics fails
      }

      // Fetch signup metrics from Firestore filtered by domain
      const usersRef = collection(db, "users");

      // Query users with email ending in the domain
      // Note: Firestore doesn't support endsWith queries directly
      // We'll need to get all users and filter, or use a different approach
      // For now, we'll get all users and filter by email domain
      const usersSnapshot = await getDocs(usersRef);

      let signupsToday = 0;
      let signupsLast7Days = 0;
      let signupsLast30Days = 0;

      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        const userEmail = userData.email || "";

        // Filter by domain
        if (!userEmail.toLowerCase().endsWith(`@${domain.toLowerCase()}`)) {
          return;
        }

        // Check for signups
        if (userData.createdAt) {
          const createdAt = userData.createdAt.toDate();

          if (createdAt >= todayStart) {
            signupsToday++;
          }
          if (createdAt >= last7DaysStart) {
            signupsLast7Days++;
          }
          if (createdAt >= last30DaysStart) {
            signupsLast30Days++;
          }
        }
      });

      const metricsData = {
        dau,
        mau,
        signupsToday,
        signupsLast7Days,
        signupsLast30Days,
      };

      setMetrics(metricsData);
      setLastUpdated(now);

      // Save metrics to analytics/orgSignups_{domain} document
      const signupsDocRef = doc(db, "analytics", `orgSignups_${domain}`);
      await setDoc(signupsDocRef, {
        ...metricsData,
        lastUpdated: now,
      });

      console.log(
        `✅ Org signups metrics saved to analytics/orgSignups_${domain}`
      );
    } catch (err) {
      console.error("Error calculating metrics:", err);
      setError("Failed to calculate metrics. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="signups-page">
        <div className="page-header">
          <h1>Organization Signups</h1>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading metrics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="signups-page">
        <div className="page-header">
          <h1>Organization Signups</h1>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={handleRefresh} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const formatLastUpdated = () => {
    if (!lastUpdated) return "";

    const now = new Date();
    const diffMs = now - lastUpdated;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60)
      return `${diffMins} minute${diffMins === 1 ? "" : "s"} ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  };

  return (
    <div className="signups-page">
      <div className="page-header">
        <div>
          <h1>Organization Signups</h1>
          {orgDomain && <p className="org-domain">Domain: {orgDomain}</p>}
          {lastUpdated && (
            <p className="last-updated">Last updated: {formatLastUpdated()}</p>
          )}
        </div>
        <button
          onClick={handleRefresh}
          className="refresh-button"
          disabled={isRefreshing}
        >
          {isRefreshing ? "⏳ Refreshing..." : "🔄 Refresh"}
        </button>
      </div>

      <div className="metrics-grid">
        <MetricCard
          title="Daily Active Users"
          value={metrics.dau}
          description="Users active today"
          color="#2AB3C0"
          icon="👤"
        />
        <MetricCard
          title="Monthly Active Users"
          value={metrics.mau}
          description="Users active in last 30 days"
          color="#FF914D"
          icon="👥"
        />
        <MetricCard
          title="Signups Today"
          value={metrics.signupsToday}
          description="New users today"
          color="#10b981"
          icon="✨"
        />
        <MetricCard
          title="Signups (7 Days)"
          value={metrics.signupsLast7Days}
          description="New users in last 7 days"
          color="#2AB3C0"
          icon="📈"
        />
        <MetricCard
          title="Signups (30 Days)"
          value={metrics.signupsLast30Days}
          description="New users in last 30 days"
          color="#FF914D"
          icon="📊"
        />
      </div>
    </div>
  );
};

const MetricCard = ({ title, value, description, color, icon }) => {
  return (
    <div className="metric-card" style={{ borderTopColor: color }}>
      <div className="metric-icon" style={{ background: `${color}20`, color }}>
        {icon}
      </div>
      <div className="metric-content">
        <h3 className="metric-title">{title}</h3>
        <div className="metric-value" style={{ color }}>
          {value.toLocaleString()}
        </div>
        <p className="metric-description">{description}</p>
      </div>
    </div>
  );
};

export default OrgDashboard;
